import { useState, useCallback } from 'react';
import axiosInstance from '../axiosInstance';
import { endpoints } from '../endPoints';
import useToggle from 'shared/hooks/use-toggle';
import { toast } from 'react-hot-toast';

const unwrapRecords = (axiosResponse) => {
  const records = axiosResponse?.data?.data;
  if (!records || typeof records !== 'object') {
    return { rows: [], total: 0, page: 0, size: 0 };
  }
  return {
    rows: Array.isArray(records.data) ? records.data : [],
    total: Number(records.records) || 0,
    page: records.page ?? 0,
    size: records.size ?? 0,
  };
};

const useGroupService = () => {
  const [groups, setGroups] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, toggleLoading] = useToggle();

  const fetchGroups = useCallback(
    async (page, size, status = null) => {
      toggleLoading();
      try {
        const base = endpoints.groups.list;
        const url =
          status != null && String(status).trim() !== ''
            ? `${base}?page=${page}&size=${size}&status=${encodeURIComponent(status)}`
            : `${base}?page=${page}&size=${size}`;
        const response = await axiosInstance.get(url);
        const { rows, total } = unwrapRecords(response);
        setGroups(rows);
        setTotalRecords(total);
      } catch (error) {
        const msg =
          error.response?.data?.message ||
          error.message ||
          'Failed to load groups.';
        toast.error(typeof msg === 'string' ? msg : 'Failed to load groups.');
        console.error('Fetch groups error:', error);
        setGroups([]);
        setTotalRecords(0);
      } finally {
        toggleLoading();
      }
    },
    [toggleLoading]
  );

  const createGroup = useCallback(async (payload) => {
    const response = await axiosInstance.post(endpoints.groups.list, payload);
    return response.data?.data ?? response.data;
  }, []);

  const updateGroup = useCallback(async (payload) => {
    const response = await axiosInstance.put(endpoints.groups.list, payload);
    return response.data?.data ?? response.data;
  }, []);

  const getGroup = useCallback(async (id) => {
    const response = await axiosInstance.get(`${endpoints.groups.list}/${id}`);
    return response.data?.data ?? response.data;
  }, []);

  const changeGroupStatus = useCallback(async (id, status) => {
    const response = await axiosInstance.post(
      `${endpoints.groups.status}?id=${encodeURIComponent(id)}&status=${encodeURIComponent(status)}`
    );
    return response.data?.data ?? response.data;
  }, []);

  /** @returns {Promise<string[]>} permission UUIDs assigned to the group */
  const getGroupPermissionIds = useCallback(async (groupId) => {
    const response = await axiosInstance.get(`${endpoints.groups.list}/${groupId}/permissions`);
    const list = response.data?.data;
    return Array.isArray(list) ? list.map((id) => String(id)) : [];
  }, []);

  const setGroupPermissions = useCallback(async (groupId, permissionIds) => {
    const response = await axiosInstance.post(
      `${endpoints.groups.list}/${groupId}/permissions`,
      { permissionIds: permissionIds ?? [] }
    );
    return response.data?.data ?? response.data;
  }, []);

  /** @returns {Promise<Array<{ userId: string, name: string, email: string, status: string }>>} */
  const getGroupMembers = useCallback(async (groupId) => {
    const response = await axiosInstance.get(`${endpoints.groups.list}/${groupId}/users`);
    const list = response.data?.data;
    return Array.isArray(list) ? list : [];
  }, []);

  /** @returns {Promise<string[]>} group UUIDs the user belongs to */
  const getGroupsForUser = useCallback(async (userId) => {
    const response = await axiosInstance.get(
      `${endpoints.groups.list}/users/${encodeURIComponent(String(userId))}/groups`
    );
    const list = response.data?.data;
    return Array.isArray(list) ? list.map((id) => String(id)) : [];
  }, []);

  /** Replaces the user’s group list. */
  const setUserGroups = useCallback(async (userId, groupIds) => {
    await axiosInstance.post(
      `${endpoints.groups.list}/users/${encodeURIComponent(String(userId))}/groups`,
      { groupIds: (groupIds ?? []).map((id) => String(id)) }
    );
  }, []);

  const fetchUsersPermissionsReport = useCallback(async () => {
    const response = await axiosInstance.get(endpoints.groups.usersPermissionsReport);
    return response.data?.data ?? response.data;
  }, []);

  /**
   * Adds one group to the user’s memberships (keeps existing groups).
   * @returns {Promise<{ alreadyMember: boolean }>}
   */
  const addUserToGroup = useCallback(
    async (userId, groupId) => {
      const uid = String(userId);
      const gid = String(groupId);
      const existing = await getGroupsForUser(uid);
      if (existing.includes(gid)) {
        return { alreadyMember: true };
      }
      await setUserGroups(uid, [...existing, gid]);
      return { alreadyMember: false };
    },
    [getGroupsForUser, setUserGroups]
  );

  return {
    groups,
    totalRecords,
    isLoading,
    fetchGroups,
    createGroup,
    updateGroup,
    getGroup,
    changeGroupStatus,
    getGroupPermissionIds,
    setGroupPermissions,
    getGroupMembers,
    getGroupsForUser,
    setUserGroups,
    addUserToGroup,
    fetchUsersPermissionsReport,
  };
};

export default useGroupService;
